import React from 'react';
import cx from 'classnames';
import ContextualSupportContainer from '../../support/ContextualSupportContainer';
import Button from '../editors/mui/Button';
import FormHeaderStages from './FormHeaderStages';
import { Stack } from 'new/elements/Stack';
import Typography from 'new/elements/Typography';
import { CircularProgress } from '@mui/material';
import { NexlaContextualMenu } from 'new/components/NexlaContextualMenu/NexlaContextualMenu';

import s from './FormHeader.module.scss';

interface ButtonType {
  label: string;
  icon?: React.ReactNode;
  variant?: 'primary' | 'text' | 'outlined' | 'contained' | 'squared' | 'ghost';
  buttonTheme?: string;
}

interface StageComponentType {
  nodeTitle: string;
  showBackButton: boolean;
  nextButtons: ButtonType[];
  disabled?: boolean;
}

interface Props {
  activeStageIndex: number;
  stageComponents: StageComponentType[];
  alwaysShowCancel: boolean;
  cancelButtonText?: string;
  shouldRenderNextButton?: boolean;
  title: string;
  subtitle?: string;
  onBackButtonClicked: () => void;
  /** Jump directly to an earlier stage (enables clicking any prior step). */
  onStageClick?: (stageIndex: number) => void;
  onCancelButtonClicked: () => void;
  onNextButtonClicked: (button: ButtonType) => void;
  awaitingValidation: boolean;
  hideContextualMenu?: boolean;
}

interface State {
  clickedButtonKey: string;
}

class FormHeader extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      clickedButtonKey: '',
    };
  }

  setClickedButtonKey(key = '') {
    this.setState({ clickedButtonKey: key });
  }

  renderButtonContent(button: ButtonType) {
    const { label, icon } = button;
    const { clickedButtonKey } = this.state;
    const { awaitingValidation } = this.props;

    const isButtonClicked = awaitingValidation && label === clickedButtonKey;

    return (
      <div className={s.container}>
        {isButtonClicked && (
          <CircularProgress
            size={14}
            thickness={5}
            color="inherit"
            className={`${s.progress} ${isButtonClicked ? s.visible : ''}`}
          />
        )}
        <span className={cx(s.btn_text, { [s.hidden]: isButtonClicked })}>
          {icon && <span className="icon">{icon}</span>}
          {label}
        </span>
      </div>
    );
  }

  getActiveStageComponent(): StageComponentType {
    const { stageComponents, activeStageIndex } = this.props;
    return (
      stageComponents?.[activeStageIndex] || {
        nodeTitle: '',
        showBackButton: false,
        nextButtons: [],
      }
    );
  }

  /**
   * Gets the logo and title class name based on whether stages are rendered.
   * @returns The class name string
   *
   * if renderStages: [flex-1, flex-1, flex-1]
   * if !renderStages: [flex-2, flex-1]
   */
  getLogoAndTitleClass(): string {
    const numStages = this.props.stageComponents?.length || 0;
    const renderStages = numStages > 1;
    return cx('logo-and-title', { 'flex-2': !renderStages });
  }

  render() {
    const {
      activeStageIndex,
      stageComponents,
      alwaysShowCancel,
      cancelButtonText = 'Discard Changes',
      shouldRenderNextButton = true,
      hideContextualMenu = false,
    } = this.props;

    const numStages = stageComponents?.length || 0;
    const activeStageComponent = this.getActiveStageComponent();
    const previousStageDisabled = stageComponents?.[activeStageIndex - 1]?.disabled === true;
    const renderStages = numStages > 1;
    const { nextButtons: stageNavigationButtons, showBackButton: showBackFromStage } =
      activeStageComponent;
    const isBackActionAllowed = showBackFromStage && !previousStageDisabled;

    return (
      <div className="form-header">
        <div className="form-header-content">
          <div className={this.getLogoAndTitleClass()}>
            {hideContextualMenu ? null : <NexlaContextualMenu />}
            <Stack direction="row" alignItems="center" gap="24px">
              <Stack alignItems="flex-start">
                <Typography variant="bodyLarge">{this.props.title}</Typography>
                {!!this.props.subtitle ? (
                  <Typography className="subtitle">{this.props.subtitle}</Typography>
                ) : null}
              </Stack>
            </Stack>
          </div>

          {renderStages && (
            <FormHeaderStages
              numStages={numStages}
              isBackActionAllowed={isBackActionAllowed}
              stageComponents={this.props.stageComponents}
              activeStageIndex={this.props.activeStageIndex}
              onBackButtonClicked={this.props.onBackButtonClicked}
              onStageClick={this.props.onStageClick}
            />
          )}

          <div className="nav-buttons-container">
            {(activeStageIndex < numStages - 1 || alwaysShowCancel) && (
              <Button
                variant="outlined"
                color="basic"
                data-testid="form-header-cancel-button"
                onClick={this.props.onCancelButtonClicked}
              >
                {cancelButtonText}
              </Button>
            )}

            {shouldRenderNextButton &&
              activeStageIndex <= numStages &&
              stageNavigationButtons.map((button) => {
                return (
                  <Button
                    color="primary"
                    key={button.label}
                    variant={button.variant || 'ghost'}
                    disabled={this.props.awaitingValidation}
                    buttonTheme={button.buttonTheme || 'primary'}
                    data-testid="form-header-next-button"
                    onClick={() => {
                      this.setClickedButtonKey(button.label);
                      this.props.onNextButtonClicked(button);
                    }}
                  >
                    {this.renderButtonContent(button)}
                  </Button>
                );
              })}

            <ContextualSupportContainer />
          </div>
        </div>
      </div>
    );
  }
}

export default FormHeader;
